/**
 * Company regulatory statement (ESMA ESG rating provider registration) - 2026-10-23.
 *
 * The statement is about the COMPANY (Varuna Sentinels B.V.), not about MPS supplier data: MPS shows no ESG
 * rating and supplier sustainability information is self-declared. Keep it visually and structurally separate
 * from supplier sustainability data (never next to the "Sustainability info provided" badge) and never call
 * supplier data a "rating".
 *
 * Values: ESMA's list of registered ESG rating providers (updated 16 October 2026), row 33, "Registered under
 * Article 5", effective 08/09/2026. ESMA's list has no separate registration number; the identifier is the LEI.
 *
 * Empty-hides rule: if ANY value is empty or malformed, buildEsmaStatement() returns null and nothing is shown.
 * Text: translation keys COMPANY_STATEMENT.ESMA (params {{date}}, {{lei}}) and COMPANY_STATEMENT.ESMA_LINK.
 */
export interface EsmaStatementConfig {
  ESMA_LEI: string;
  ESMA_EFFECTIVE_DATE: string; // ISO yyyy-mm-dd
  ESMA_REGISTER_URL: string;
}

export const COMPANY_STATEMENT_CONFIG: EsmaStatementConfig = {
  ESMA_LEI: '724500F8TH8X80MBDV85',
  ESMA_EFFECTIVE_DATE: '2026-09-08',
  ESMA_REGISTER_URL: 'https://www.esma.europa.eu/sites/default/files/2026-08/List_of_registered_ESG_providers.pdf',
};

export interface EsmaStatement {
  lei: string;
  /** The effective date, formatted for the given language (e.g. "8 September 2026"). */
  date: string;
  url: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LEI = /^[A-Z0-9]{20}$/;
const HTTPS_URL = /^https:\/\/[^\s"'<>]+$/;

/** The statement parameters, or null when any value is missing or malformed (show nothing). */
export function buildEsmaStatement(cfg: Partial<EsmaStatementConfig> | null | undefined, lang = 'en'): EsmaStatement | null {
  const lei = String(cfg?.ESMA_LEI ?? '').trim();
  const iso = String(cfg?.ESMA_EFFECTIVE_DATE ?? '').trim();
  const url = String(cfg?.ESMA_REGISTER_URL ?? '').trim();
  const m = ISO_DATE.exec(iso);
  if (!LEI.test(lei) || !m || !HTTPS_URL.test(url)) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  const locale = lang === 'en' ? 'en-GB' : lang;
  let date: string;
  try {
    date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  } catch {
    date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  }
  return { lei, date, url };
}
